import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { supabase, supabaseReady } from "../lib/supabase";
import { lastUsedBabyId } from "../lib/babyId";
import { useAuth } from "../lib/AuthContext";
import { useT } from "../lib/LocaleContext";
import { useEvents, type EventsApi } from "../lib/useEvents";

export type Baby = {
  id: string;
  name: string;
  birthday: string;
  due_date: string | null;
  pediatrician_cleared_stretching: boolean;
  photo_data: string | null;
  bottle_default_oz: number | null;
  nurse_default_oz: number | null;
};

export type BabyFeedDefaults = Pick<Baby, "bottle_default_oz" | "nurse_default_oz">;

export type CaretakerRole = "creator" | "caretaker";

export type BabyShellContext = {
  baby: Baby;
  role: CaretakerRole;
  /** How many babies this account can open — gates the "Switch baby" menu item. */
  babyCount: number;
  events: EventsApi;
  refetchBaby: () => Promise<void>;
  updateBabyPhoto: (dataUrl: string) => Promise<void>;
  updateBabyName: (name: string) => Promise<void>;
  updateBabyDefaults: (fields: Partial<BabyFeedDefaults>) => Promise<void>;
};

export function BabyShell() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();
  const t = useT();
  const [baby, setBaby] = useState<Baby | null>(null);
  const [role, setRole] = useState<CaretakerRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [babyCount, setBabyCount] = useState(1);
  const events = useEvents(id);
  const activeScope = useRef<{ id: string; userId: string | undefined } | null>(null);

  async function fetchBaby(background = false) {
    const scope = activeScope.current;
    const isCurrent = () => scope !== null && activeScope.current === scope &&
      scope.id === id && scope.userId === user?.id;
    if (!isCurrent()) return;
    const { data, error: fetchError } = await supabase
      .from("babies")
      .select("id, name, birthday, due_date, pediatrician_cleared_stretching, photo_data, bottle_default_oz, nurse_default_oz")
      .eq("id", id)
      .maybeSingle();
    if (!isCurrent()) return;
    if (fetchError) {
      if (background) return; // Keep the last loaded view while temporarily offline.
      setError(fetchError.message);
      setLoading(false);
      return;
    }
    if (!data) {
      // Under RLS a non-member's select returns nothing — that's the no-access case.
      // Drop the stale pointer so Landing doesn't keep bouncing back here.
      lastUsedBabyId.clear(user?.id);
      setNoAccess(true);
      setLoading(false);
      return;
    }
    // Membership row also carries the creator/caretaker role used to gate the UI.
    const { data: member, error: memberError } = await supabase
      .from("baby_caretakers")
      .select("role")
      .eq("baby_id", id)
      .eq("user_id", user?.id ?? "")
      .maybeSingle();
    if (!isCurrent()) return;
    if (memberError) {
      if (!background) { setError(memberError.message); setLoading(false); }
      return;
    }
    if (!member) {
      lastUsedBabyId.clear(user?.id);
      setNoAccess(true);
      setLoading(false);
      return;
    }
    // Cheap head count of this account's memberships — the Today menu only
    // offers "Switch baby" when there is somewhere else to switch to.
    const { count } = await supabase
      .from("baby_caretakers")
      .select("baby_id", { count: "exact", head: true })
      .eq("user_id", user?.id ?? "");
    if (!isCurrent()) return;
    setBabyCount(count ?? 1);
    setNoAccess(false);
    setError(null);
    setBaby(data as Baby);
    setRole(member.role as CaretakerRole);
    lastUsedBabyId.set(user?.id, id);
    setLoading(false);
  }

  async function updateBabyName(name: string) {
    setBaby((prev) => (prev ? { ...prev, name } : prev));
    const { error: updateError } = await supabase.from("babies").update({ name }).eq("id", id);
    if (updateError) {
      console.error("[BabyShell] name update failed", updateError);
      await fetchBaby();
    }
  }

  async function updateBabyDefaults(fields: Partial<BabyFeedDefaults>) {
    setBaby((prev) => (prev ? { ...prev, ...fields } : prev));
    const { error: updateError } = await supabase.from("babies").update(fields).eq("id", id);
    if (updateError) {
      console.error("[BabyShell] feed defaults update failed", updateError);
      await fetchBaby();
    }
  }

  async function updateBabyPhoto(dataUrl: string) {
    setBaby((prev) => (prev ? { ...prev, photo_data: dataUrl } : prev));
    const { error: updateError } = await supabase.rpc("set_baby_photo", { p_baby_id: id, p_photo: dataUrl });
    if (updateError) {
      console.error("[BabyShell] photo update failed", updateError);
      await fetchBaby();
    }
  }

  useEffect(() => {
    if (!id) return;
    if (!supabaseReady) {
      setError(t.babyShell.storageError);
      setLoading(false);
      return;
    }
    const scope = { id, userId: user?.id };
    activeScope.current = scope;
    let cancelled = false;
    let refreshing = false;
    const refresh = async (background = false) => {
      if (cancelled || refreshing) return;
      refreshing = true;
      try { await fetchBaby(background); }
      finally { refreshing = false; }
    };
    const onReturn = () => {
      if (document.visibilityState === "visible") void refresh(true);
    };
    void refresh();
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      cancelled = true;
      if (activeScope.current === scope) activeScope.current = null;
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  if (loading) {
    return (
      <main className="screen screen--center">
        <p className="muted">{t.common.loading}</p>
      </main>
    );
  }

  if (noAccess) {
    return (
      <main className="screen screen--center">
        <h1>{t.babyShell.noAccessTitle}</h1>
        <p className="hero__sub">{t.babyShell.noAccessBody}</p>
        <Link className="btn" to="/new">
          {t.babyShell.startNew}
        </Link>
      </main>
    );
  }

  if (error || !baby || !role) {
    return (
      <main className="screen screen--center">
        <h1>{t.babyShell.errorTitle}</h1>
        <p className="error">{error ?? t.common.somethingWrong}</p>
        <Link className="btn" to="/new">
          {t.babyShell.startOver}
        </Link>
      </main>
    );
  }

  const context: BabyShellContext = {
    baby,
    role,
    babyCount,
    events,
    refetchBaby: fetchBaby,
    updateBabyPhoto,
    updateBabyName,
    updateBabyDefaults,
  };

  return (
    <div className="baby-shell">
      <Outlet context={context} />
    </div>
  );
}
